import * as Sentry from '@sentry/react';
import { Amplify } from 'aws-amplify';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { syncCookieConsentWithCognito } from './utils/cookieConsentSync';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { awsconfig } from './aws-exports.ts';

import ScrollToTop from '@/components/ScrollToTop/ScrollToTop';
import { AppContext } from './context/Context';
import { CustomRoutes } from './router/AppRouter';
import Footer from '@/components/Home/Footer';
import { AlertManager } from './modules/alerts/components/AlertManager';
import { AlertModalProvider } from './modules/alerts/context/AlertModalContext';
import { AcademicExpirationProvider } from './context/AcademicExpirationContext';
import { TextCmsProvider } from './context/TextCmsContext';
import GlobalTrialBanner from '@/components/Layout/GlobalTrialBanner';
import PolicyUpdateModal from '@/components/Cookies/PolicyUpdateModal';
import ChatWidget from './components/ChatWidget/index';
import RenewalConfirmationModal from '@/components/Academic/RenewalConfirmationModal';
import { useRenewalConfirmation } from './hooks/useRenewalConfirmation';

// Migration utilities
import { MigrationProvider } from './store/migration/MigrationProvider';
import { AuthMigrationWrapper } from './store/migration/AuthMigrationWrapper';

// Extend Window interface
declare global {
  interface Window {
    syncCookieConsentWithCognito?: typeof syncCookieConsentWithCognito;
  }
}

// Configure Amplify in index file or root file
Amplify.configure(awsconfig as any);

if (typeof window !== 'undefined') {
  window.syncCookieConsentWithCognito = syncCookieConsentWithCognito;
}

// Configure Sentry
if (process.env.NODE_ENV !== 'production') {
  Sentry.init({
    dsn: 'https://aba19d681a08769aed34511cdaf4a06b@o4506586512228352.ingest.us.sentry.io/4506586513997824',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      // 'localhost',
      // /^https:\/\/disgenet\.com/,
      /^https:\/\/test\.medbioinformatics\.com/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

// Suppress known warnings
const error = console.error;
console.error = (...args: any[]) => {
  // https://github.com/recharts/recharts/issues/3615
  if (/defaultProps/.test(args[0])) return;
  //error(...args);
  Sentry.captureMessage(args.join(' '), 'error');
};

export const App = () => {
  const { showConfirmationModal, confirmationMessage, closeConfirmationModal } = useRenewalConfirmation();

  return (
    <AuthMigrationWrapper>
      {({ contextValue, currentUser }) => (
        <React.Fragment>
          <AppContext.Provider value={contextValue}>
            <AcademicExpirationProvider
              user={contextValue.user}
              isAuthenticated={contextValue.isAuthenticated}
            >
              <CustomRoutes />
              <GlobalTrialBanner />
              <PolicyUpdateModal
                isAuthenticated={contextValue.isAuthenticated}
                userId={contextValue.user?.sub || contextValue.user?.username}
              />
              <RenewalConfirmationModal
                open={showConfirmationModal}
                onClose={closeConfirmationModal}
                message={confirmationMessage}
              />
              <ScrollToTop />
              <div className="row">
                <AlertManager userInfo={currentUser} variant="pink" />
              </div>
              <ChatWidget />
              <Footer />
            </AcademicExpirationProvider>
          </AppContext.Provider>
        </React.Fragment>
      )}
    </AuthMigrationWrapper>
  );
};

// handle Cloudfront redirects before app mount
if (window.location.hash.includes('#!')) {
  const newPath = (/#!(\/.*)$/.exec(window.location.hash) || [])[1];
  history.replaceState(history.state, '', newPath);
}

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');
createRoot(root).render(
  <BrowserRouter>
    <React.StrictMode>
      <HelmetProvider>
        <AlertModalProvider>
          <TextCmsProvider locale="en">
            <MigrationProvider
              initialFlags={{
                useAuthStore: true, // Start with auth migration
                useRestrictionsStore: false,
                useNavigationStore: false,
                useTableStore: false,
                useInfoSidebarStore: false,
              }}
            >
              <App />
            </MigrationProvider>
          </TextCmsProvider>
        </AlertModalProvider>
      </HelmetProvider>
    </React.StrictMode>
  </BrowserRouter>,
);
